import * as React from "react";
import { cn } from "../../shared/ui/index.ts";
import { useNavigate, useRouterState } from "@tanstack/react-router";
import { APP_NAME } from "../app-meta.ts";
import {
  activeNavId,
  navItemById,
  parseUserModuleRoute,
  PATIENT_MODULE_NAV,
  replacePracticeInPath,
  usePracticeUuid,
} from "../services/navigation.service.ts";
import {
  useModulesConfig,
  type UserModule,
} from "../services/modules-config.service.ts";
import { usePractice } from "../providers/practice.provider.tsx";
import { Interactions } from "../providers/interactions.provider.tsx";
import {
  PRACTICE_ACCENTS,
  type Practice,
} from "../services/practices.service.ts";
import { AppLogo } from "../../shared/brand/app-logo.tsx";
import { I } from "../../shared/icons/icons.tsx";
import { shellIconBtn } from "../../shared/page-layout.ts";

const PRACTITIONER = {
  name: "Dr. Camille Moreau",
  role: "Orthodontiste",
  email: "camille.moreau@ligam.fr",
  initials: "CM",
} as const;

function resolveModuleLabel(
  pathname: string,
  activeId: string,
  practiceUuid: string,
  userModules: UserModule[],
): string {
  const userModuleId = parseUserModuleRoute(pathname);
  if (userModuleId) {
    const mod = userModules.find((m) => m.id === userModuleId);
    if (mod) return mod.label;
  }
  if (activeId.startsWith("patient-")) {
    const moduleId = activeId.slice("patient-".length);
    const mod = PATIENT_MODULE_NAV.find((m) => m.id === moduleId);
    if (mod) return mod.label;
  }
  const navItem = navItemById(activeId, practiceUuid);
  return navItem ? navItem.label : "Tableau de bord";
}

function TopBar({
  onToggleSidebar,
  sidebarCollapsed,
}: {
  onToggleSidebar: () => void;
  sidebarCollapsed: boolean;
}) {
  const [searchFocus, setSearchFocus] = React.useState(false);
  const [userOpen, setUserOpen] = React.useState(false);
  const userRef = React.useRef<HTMLDivElement | null>(null);
  const dialog = Interactions.useDialog();
  const patient = Interactions.usePatient();
  const navigate = useNavigate();
  const pathname = useRouterState({ select: (s) => s.location.pathname });
  const { practice, practices } = usePractice();
  const practiceUuid = usePracticeUuid();
  const activeId = activeNavId(pathname, practiceUuid);
  const modulesConfig = useModulesConfig();
  const moduleLabel = resolveModuleLabel(
    pathname,
    activeId,
    practiceUuid,
    modulesConfig.userModules,
  );
  const dateLong = new Date().toLocaleDateString("fr-FR", {
    weekday: "long",
    day: "numeric",
    month: "long",
    year: "numeric",
  });

  React.useEffect(() => {
    const onDoc = (e: MouseEvent) => {
      if (userRef.current && !userRef.current.contains(e.target as Node))
        setUserOpen(false);
    };
    document.addEventListener("mousedown", onDoc);
    return () => document.removeEventListener("mousedown", onDoc);
  }, []);

  const selectPractice = (uuid: string) => {
    if (uuid === practice.uuid) return;
    patient.clearPatient();
    navigate({ to: replacePracticeInPath(pathname, uuid) });
    setUserOpen(false);
  };

  const sidebarToggleBtn = shellIconBtn;

  return (
    <header className="app-top-bar relative z-30 border-b border-sf-border bg-sf-surface">
      <div className="app-top-bar__inner relative flex min-h-[var(--shell-top-offset,var(--topbar-h))] w-full min-w-0 items-center gap-2 px-2 pt-[var(--shell-safe-top,env(safe-area-inset-top,0px))] sm:gap-3 sm:px-3 lg:min-h-[var(--topbar-h)] lg:gap-4 lg:px-4 lg:pt-0">
        <button
          type="button"
          className={cn("focus-ring shrink-0", sidebarToggleBtn)}
          aria-label={sidebarCollapsed ? "Ouvrir le menu" : "Fermer le menu"}
          onClick={onToggleSidebar}
        >
          <I.PanelLeft size={18} panelOpen={!sidebarCollapsed} />
        </button>

        {/* Titre + date masqués sur mobile et tablette (manque de place
         * à cause de la search centrée). N'apparaissent qu'à partir de lg. */}
        <div className="hidden min-w-0 shrink flex-col justify-center gap-px leading-tight lg:flex">
          <h1 className="m-0 truncate text-[15px] font-bold leading-tight text-sf-text">
            {moduleLabel}
          </h1>
          <p className="m-0 truncate text-[11.5px] capitalize leading-tight text-sf-text-muted">
            {dateLong}
          </p>
        </div>

        {/* Barre de recherche centrée par rapport à l'écran (absolue, ne bouge pas).
         * On démarre après la safe-area top (même inset que le padding du top bar)
         * pour rester aligné verticalement avec le toggle et l'avatar. */}
        <div className="pointer-events-none absolute inset-x-0 bottom-0 top-[var(--shell-safe-top,env(safe-area-inset-top,0px))] flex items-center justify-center lg:top-0">
          <div className="pointer-events-auto w-full max-w-44 sm:max-w-80 lg:max-w-lg">
            <SearchTrigger
              onOpen={() => dialog.openPalette()}
              searchFocus={searchFocus}
              onFocus={() => setSearchFocus(true)}
              onBlur={() => setSearchFocus(false)}
              className="w-full"
            />
          </div>
        </div>

        <div className="ml-auto flex shrink-0 items-center gap-0.5 sm:gap-1">
          <div ref={userRef} className="relative">
            <button
              type="button"
              className="focus-ring flex h-10 cursor-pointer items-center gap-2 rounded-lg border-none bg-transparent py-0 pl-1 pr-1.5 transition-colors hover:bg-gray-100 lg:h-[38px] lg:rounded-md lg:pl-1.5 lg:pr-2"
              onClick={() => setUserOpen(!userOpen)}
              aria-label="Menu utilisateur et cabinet"
              aria-expanded={userOpen}
            >
              <div
                className="flex h-8 w-8 shrink-0 items-center justify-center rounded-full text-[11px] font-semibold tracking-wide text-white lg:h-7 lg:w-7"
                style={{
                  background: `linear-gradient(135deg, color-mix(in srgb, var(--sf-primary) 75%, white) 0%, var(--sf-primary) 100%)`,
                }}
              >
                {PRACTITIONER.initials}
              </div>
              <div className="hidden min-w-0 max-w-[140px] flex-col items-start leading-snug lg:flex xl:max-w-[180px]">
                <span className="w-full truncate text-[13px] font-semibold text-sf-text">
                  {PRACTITIONER.name}
                </span>
                <span className="flex w-full min-w-0 items-center gap-1 text-[11px] text-sf-text-muted">
                  <span
                    className="h-1.5 w-1.5 shrink-0 rounded-full"
                    style={{
                      background: PRACTICE_ACCENTS[practice.accent].primary,
                    }}
                  />
                  <span className="truncate">Cabinet {practice.name}</span>
                </span>
              </div>
              <I.ChevronDown
                size={14}
                className="ml-0.5 hidden text-sf-text-muted lg:block"
              />
            </button>

            {userOpen && (
              <div className="absolute right-0 top-[calc(100%+6px)] z-50 w-[min(320px,calc(100vw-1rem))] overflow-hidden rounded-[var(--sf-radius-lg)] border border-sf-border bg-white py-1 shadow-[var(--sf-shadow-lg)]">
                <div className="border-b border-sf-border px-3.5 py-3">
                  <div className="text-[13px] font-semibold">
                    {PRACTITIONER.name}
                  </div>
                  <div className="text-xs text-sf-text-muted">
                    {PRACTITIONER.email}
                  </div>
                  <div className="mt-1 text-[11px] text-sf-text-muted">
                    {PRACTITIONER.role}
                  </div>
                </div>

                <PracticePicker
                  practices={practices}
                  current={practice}
                  onSelect={selectPractice}
                />

                <MenuItem icon={<I.Settings size={15} />} label="Paramètres" />
                <MenuItem icon={<I.Help size={15} />} label="Aide & support" />
                <div className="my-1 h-px bg-sf-border" />
                <MenuItem
                  icon={<I.User size={15} />}
                  label="Espace patient"
                  onClick={() => {
                    window.location.href = "/patient";
                  }}
                />
                <div className="my-1 h-px bg-sf-border" />
                <MenuItem
                  icon={<I.X size={15} />}
                  label="Se déconnecter"
                  danger
                />
              </div>
            )}
          </div>
        </div>
      </div>
    </header>
  );
}

function PracticePicker({
  practices,
  current,
  onSelect,
}: {
  practices: Practice[];
  current: Practice;
  onSelect: (id: string) => void;
}) {
  return (
    <div className="border-b border-sf-border px-2 py-2">
      <div className="px-2 pb-1.5 text-[10.5px] font-bold uppercase tracking-wide text-sf-text-muted">
        Cabinet
      </div>
      <div className="flex max-h-[min(50vh,280px)] flex-col gap-px overflow-y-auto">
        {practices.map((cab) => {
          const accent = PRACTICE_ACCENTS[cab.accent];
          const active = cab.uuid === current.uuid;
          return (
            <button
              key={cab.uuid}
              type="button"
              onClick={() => onSelect(cab.uuid)}
              className={cn(
                "flex w-full cursor-pointer items-center gap-2.5 rounded-md border-none px-2 py-2 text-left transition-colors",
                active ? "bg-sf-primary-50" : "bg-transparent hover:bg-gray-50",
              )}
            >
              <span
                className="h-2.5 w-2.5 shrink-0 rounded-full ring-2 ring-white"
                style={{
                  background: accent.primary,
                  boxShadow: `0 0 0 1px ${accent.lighter}`,
                }}
              />
              <div className="min-w-0 flex-1">
                <div
                  className={cn(
                    "truncate text-[12.5px] text-sf-text",
                    active ? "font-semibold text-sf-primary" : "font-medium",
                  )}
                >
                  Cabinet {cab.name}
                </div>
                <div className="truncate text-[10.5px] text-sf-text-muted">
                  {cab.city}
                </div>
              </div>
              {active ? (
                <I.Check size={15} className="shrink-0 text-sf-primary" />
              ) : null}
            </button>
          );
        })}
      </div>
    </div>
  );
}

function SearchTrigger({
  onOpen,
  searchFocus,
  onFocus,
  onBlur,
  className,
}: {
  onOpen: () => void;
  searchFocus: boolean;
  onFocus: () => void;
  onBlur: () => void;
  className?: string;
}) {
  return (
    <button
      type="button"
      onClick={onOpen}
      onFocus={onFocus}
      onBlur={onBlur}
      aria-label={`Rechercher (${APP_NAME})`}
      className={cn(
        "flex h-10 cursor-pointer items-center gap-2.5 rounded-lg border bg-sf-surface py-0 pl-1 pr-3 text-left font-[inherit] transition-[border-color,box-shadow] lg:h-9 lg:rounded-[var(--sf-radius)]",
        searchFocus
          ? "border-sf-primary shadow-[0_0_0_3px_color-mix(in_srgb,var(--sf-primary)_15%,transparent)]"
          : "border-sf-border shadow-none",
        className,
      )}
    >
      <AppLogo size={28} decorative className="h-7 w-7 p-0.5" />
      <span className="flex-1 truncate text-[13.5px] text-sf-text-muted">
        Rechercher…
      </span>
      <kbd className="hidden rounded border border-sf-border bg-sf-surface-2 p-1 font-[inherit] text-[11px] font-medium leading-none text-sf-text-muted xl:inline">
        ⌘K
      </kbd>
    </button>
  );
}

function MenuItem({
  icon,
  label,
  danger,
  onClick,
}: {
  icon: React.ReactNode;
  label: string;
  danger?: boolean;
  onClick?: () => void;
}) {
  return (
    <button
      type="button"
      onClick={onClick}
      className={cn(
        "flex w-full cursor-pointer items-center gap-2.5 border-none bg-transparent px-3.5 py-2 text-left text-[13px] transition-colors hover:bg-gray-50",
        danger ? "text-sf-danger" : "text-sf-text",
      )}
    >
      {icon}
      <span>{label}</span>
    </button>
  );
}

export { TopBar };
